import { useState, useRef, useEffect } from 'react';
import { usePhotoGenerate } from '@genfun/core/hooks/usePhotoGenerate';
import { useVideoAnimate } from '@genfun/core/hooks/useVideoAnimate';
import { LoginPopup } from '../popups/LoginPopup';
import { SubscriptionPopup } from '../popups/SubscriptionPopup';
import { AccountPopup } from '../popups/AccountPopup';
import { SiteFooter } from '../components/SiteFooter';
import { downloadFile } from '../utils/download';
// Base neo-brutalist admin styles (chips, hero, etc.)
import './EditAdmin.css';
// Page-specific overrides (compact prompt chips) — must load after EditAdmin.css
import './TextGenerate.css';

function HeaderMenu({ user, onNavigate, onSignOut }) {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const onDocDown = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDocDown);
        document.addEventListener('touchstart', onDocDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocDown);
            document.removeEventListener('touchstart', onDocDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    return (
        <div className="adminHeaderMenu" ref={wrapRef}>
            <button
                className={`adminHeaderMenuBtn${open ? ' adminHeaderMenuBtn--open' : ''}`}
                onClick={() => setOpen(o => !o)}
                aria-label="More menu"
                aria-expanded={open}
            >
                <span aria-hidden="true">⋯</span>
            </button>
            {open && (
                <div className="adminHeaderMenuDropdown" role="menu">
                    <button className="adminHeaderMenuItem" role="menuitem" onClick={() => { setOpen(false); onNavigate('video'); }}>Generate video</button>
                    <button className="adminHeaderMenuItem" role="menuitem" onClick={() => { setOpen(false); onNavigate('photo'); }}>Generate photo</button>
                    <button className="adminHeaderMenuItem" role="menuitem" onClick={() => { setOpen(false); onNavigate('gallery'); }}>Gallery</button>
                    {user && (
                        <button
                            className="adminHeaderMenuItem adminHeaderMenuItem--danger"
                            role="menuitem"
                            onClick={() => { setOpen(false); onSignOut(); }}
                        >
                            Sign out
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

const GEN_MESSAGES = ['Reading your prompt', 'Composing the scene', 'Rendering pixels', 'Almost there'];

function ProgressBar({ durationMs, startTime }) {
    const [progress, setProgress] = useState(0);
    const [msgIdx, setMsgIdx] = useState(0);
    const startRef = useRef(startTime || Date.now());
    useEffect(() => {
        const interval = setInterval(() => {
            const elapsed = Date.now() - startRef.current;
            setProgress(Math.min(95, (elapsed / durationMs) * 100));
        }, 300);
        return () => clearInterval(interval);
    }, [durationMs]);
    useEffect(() => {
        const interval = setInterval(() => {
            setMsgIdx(i => (i + 1) % GEN_MESSAGES.length);
        }, 3000);
        return () => clearInterval(interval);
    }, []);
    const secsLeft = Math.max(0, Math.round((durationMs - (Date.now() - startRef.current)) / 1000));
    return (
        <div className="adminProgressWrap">
            <div className="adminProgressBar" style={{ width: `${progress}%` }} />
            <div className="adminProgressText">{GEN_MESSAGES[msgIdx]}...</div>
            <div className="adminProgressTime">~{secsLeft}s remaining</div>
        </div>
    );
}

const ANIMATE_ACTIONS = [
    { key: 'cumshot', label: 'Cumshot' },
    { key: 'blowjob', label: 'Blowjob' },
    { key: 'posing', label: 'Posing' },
    { key: 'titfuck', label: 'Titfuck' },
];

// Prompt-suggestion chips. tone: pink = girl, orange = action, lime = scene/style.
// First INITIAL_CHIPS_COUNT show by default; rest hide behind "Show more".
const INITIAL_CHIPS_COUNT = 10;
const PROMPT_CHIPS = [
    // Initial row — quick wins
    { label: 'Hot blondie', tone: 'pink' },
    { label: 'Sexy brunette', tone: 'pink' },
    { label: 'Asian girl', tone: 'pink' },
    { label: 'Big tits', tone: 'pink' },
    { label: 'Blowjob', tone: 'orange' },
    { label: 'Cowgirl', tone: 'orange' },
    { label: 'Cumshot', tone: 'orange' },
    { label: 'Gangbang', tone: 'orange' },
    { label: 'Lingerie', tone: 'lime' },
    { label: 'On the beach', tone: 'lime' },
    // Extra — girls
    { label: 'Latina', tone: 'pink' },
    { label: 'Redhead', tone: 'pink' },
    { label: 'Goth girl', tone: 'pink' },
    { label: 'Petite', tone: 'pink' },
    { label: 'Curvy', tone: 'pink' },
    { label: 'Athletic', tone: 'pink' },
    { label: 'Tattooed', tone: 'pink' },
    { label: 'Big ass', tone: 'pink' },
    // Extra — actions / poses
    { label: 'Doggystyle', tone: 'orange' },
    { label: 'Twerking', tone: 'orange' },
    { label: 'Posing', tone: 'orange' },
    { label: 'Spreading', tone: 'orange' },
    { label: 'Stripping', tone: 'orange' },
    // Extra — scene / style
    { label: 'Bedroom', tone: 'lime' },
    { label: 'Shower', tone: 'lime' },
    { label: 'Office', tone: 'lime' },
    { label: 'Pool', tone: 'lime' },
    { label: 'Latex', tone: 'lime' },
    { label: 'Schoolgirl outfit', tone: 'lime' },
    { label: 'Stockings', tone: 'lime' },
    { label: 'POV', tone: 'lime' },
    { label: 'Anime style', tone: 'lime' },
    { label: 'Photorealistic', tone: 'lime' },
];

export function TextGenerate({ token, user, setSignedIn, onNavigate }) {
    const [prompt, setPrompt] = useState('');
    const [img, setImg] = useState(null);
    const [genType, setGenType] = useState(null);
    const [generated, setGenerated] = useState(false);
    const [taskId, setTaskId] = useState(null);
    const [showLogin, setShowLogin] = useState(false);
    const [showPricing, setShowPricing] = useState(false);
    const [showAccount, setShowAccount] = useState(false);
    const [chipsExpanded, setChipsExpanded] = useState(false);

    // Animate-from-result state
    const [animateAction, setAnimateAction] = useState(null);
    const [animateImage, setAnimateImage] = useState(null);
    const [animateTrigger, setAnimateTrigger] = useState(false);
    const [animVideo, setAnimVideo] = useState(null);
    const [animGenType, setAnimGenType] = useState(null);
    // animGenerated value isn't read in render (keyed off animGenType/animVideo); keep only the setter.
    const [, setAnimGenerated] = useState(false);
    const [animTaskId, setAnimTaskId] = useState(null);

    const formRef = useRef({ type: 'photo', number: 1 });
    const resultRef = useRef(null);
    const animRef = useRef(null);

    const { handleGenerate, error, setError } = usePhotoGenerate({
        token, formRef, textPromptActive: true, textPromptValue: prompt,
        callbacks: {
            setGenType, setGenerated, setImg, setVideo: () => {}, setTaskId,
            setAnimateAction: () => {}, setGenerationConfig: () => {},
            setShowVideoPopUp: () => {}, setShowPayPopUp: () => setShowPricing(true),
            onScrollToResults: () => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }),
            onResetInteraction: () => {},
        }
    });

    const { handleAnimate, error: animateError } = useVideoAnimate({
        token,
        user,
        selectedImage: animateImage,
        action: animateAction,
        callbacks: {
            setGenType: setAnimGenType,
            setGenerated: setAnimGenerated,
            setVideo: setAnimVideo,
            setTaskId: setAnimTaskId,
            setShowLoginPopup: () => setShowLogin(true),
            onScrollToResults: () => animRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }),
        },
    });

    useEffect(() => {
        if (!animateTrigger || !animateImage || !animateAction) return;
        setAnimateTrigger(false);
        handleAnimate();
    }, [animateTrigger, animateImage, animateAction, handleAnimate]);

    const onAnimate = (actionKey) => {
        const url = Array.isArray(img) ? img[0] : img;
        if (!url || !taskId || animGenType) return;
        setAnimVideo(null);
        setAnimGenerated(false);
        setAnimateAction(actionKey);
        setAnimateImage({ type: 'user', url, mediaId: taskId });
        setAnimateTrigger(true);
    };

    const showLoading = !!genType && !generated;
    const images = img ? (Array.isArray(img) ? img : [img]) : [];
    const showResult = images.length > 0;
    const canSubmit = prompt.trim().length > 0 && !showLoading;

    // Toggle a suggestion term in/out of the comma-separated prompt.
    const toggleChip = (label) => {
        setPrompt(prev => {
            const parts = prev.split(',').map(s => s.trim()).filter(Boolean);
            const idx = parts.findIndex(p => p.toLowerCase() === label.toLowerCase());
            if (idx >= 0) parts.splice(idx, 1);
            else parts.push(label);
            return parts.join(', ');
        });
    };
    const isChipSelected = (label) =>
        prompt.split(',').map(s => s.trim().toLowerCase()).includes(label.toLowerCase());

    const visibleChips = chipsExpanded ? PROMPT_CHIPS : PROMPT_CHIPS.slice(0, INITIAL_CHIPS_COUNT);
    const hiddenChipsCount = PROMPT_CHIPS.length - INITIAL_CHIPS_COUNT;

    const onGenerate = () => {
        if (!canSubmit) return;
        if (!user) { setShowLogin(true); return; }
        if (!user.credits) { setShowPricing(true); return; }
        setImg(null);
        setGenerated(false);
        handleGenerate();
    };

    const onReset = () => {
        setImg(null);
        setGenerated(false);
        setGenType(null);
        setTaskId(null);
        setError(null);
        setAnimateAction(null);
        setAnimateImage(null);
        setAnimVideo(null);
        setAnimGenerated(false);
        setAnimGenType(null);
        setAnimTaskId(null);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const signOut = () => {
        localStorage.removeItem('igenfuntoken');
        window.location.reload();
    };

    return (
        <div className="adminPage">
            {/* Header */}
            <div className="adminHeader">
                <div className="adminHeaderLeft">
                    <span className="adminLogo" onClick={() => onNavigate('landing')}>D33P</span>
                    <nav className="adminNav">
                        <button className="adminNavBtn adminNavBtn--admin" onClick={() => onNavigate('video')}>Generate video</button>
                        <button className="adminNavBtn adminNavBtn--active">Generate photo</button>
                        <button className="adminNavBtn" onClick={() => onNavigate('gallery')}>Gallery</button>
                    </nav>
                </div>
                <div className="adminHeaderRight">
                    {!user ? (
                        <button className="adminLoginBtn" onClick={() => setShowLogin(true)}>Log in</button>
                    ) : (
                        <>
                            <button className="adminCredits" onClick={() => setShowAccount(true)}>{user.credits ?? 0} tokens</button>
                            <button className="adminSignOutBtn adminSignOutBtn--desktopOnly" onClick={signOut}>Sign out</button>
                        </>
                    )}
                    <HeaderMenu user={user} onNavigate={onNavigate} onSignOut={signOut} />
                </div>
            </div>

            {/* Hero */}
            <section className="adminHero adminHero--compact">
                <h1 className="adminHeroTitle adminHeroTitle--compact">
                    <span className="adminHeroTitleAccent">Generate photo</span>
                </h1>
            </section>

            {/* Prompt input */}
            <div className="adminInputs" style={{ gridTemplateColumns: '1fr' }}>
                <div className="adminPromptCard">
                    <label className="adminPromptLabel" htmlFor="textGenPromptInput">
                        <span className="adminEyebrow adminEyebrow--inline">[01] YOUR PROMPT</span>
                    </label>

                    <div className="adminHintRow">
                        <span className="adminHintLabel">Prompt ideas</span>
                        <div className="adminHintGrid textGenChips">
                            {visibleChips.map(chip => (
                                <button
                                    type="button"
                                    key={chip.label}
                                    className={`adminHintChip adminHintChip--${chip.tone}${isChipSelected(chip.label) ? ' adminHintChip--selected' : ''}`}
                                    onClick={() => toggleChip(chip.label)}
                                >
                                    <span className="adminHintChipMark" aria-hidden="true">✦</span>
                                    <span className="adminHintChipText">{chip.label}</span>
                                </button>
                            ))}
                            {hiddenChipsCount > 0 && (
                                <button
                                    type="button"
                                    className="adminHintChipsMoreBtn"
                                    onClick={() => setChipsExpanded(v => !v)}
                                    aria-expanded={chipsExpanded}
                                >
                                    {chipsExpanded
                                        ? <>Show less <span aria-hidden="true">↑</span></>
                                        : <>Show more <span className="adminHintChipsMoreCount">+{hiddenChipsCount}</span> <span aria-hidden="true">↓</span></>
                                    }
                                </button>
                            )}
                        </div>
                    </div>

                    <textarea
                        id="textGenPromptInput"
                        className="adminPromptInput"
                        placeholder="Describe the photo you want to generate..."
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        rows={4}
                    />
                </div>
            </div>

            {/* Submit */}
            {!showResult ? (
                <button className="adminSubmitBtn" onClick={onGenerate} disabled={!canSubmit}>
                    {showLoading ? 'Generating…' : 'Generate'}
                </button>
            ) : (
                <button className="adminSubmitBtn" onClick={onReset}>
                    Generate another
                </button>
            )}

            {!showResult && !showLoading && (
                <div className={`adminInputsHint${canSubmit ? '' : ' adminInputsHint--missing'}`}>
                    {canSubmit ? (
                        <>
                            <span>1 TOKEN</span>
                            <span className="adminInputsHintDot">/</span>
                            <span>~80S RENDER</span>
                            <span className="adminInputsHintDot">/</span>
                            <span>HD</span>
                        </>
                    ) : (
                        <>
                            <span className="adminInputsHintArrow">↑</span>
                            <span>Write a prompt to continue</span>
                        </>
                    )}
                </div>
            )}

            {/* Result */}
            <div ref={resultRef} />
            {(showLoading || showResult || error) && (
                <div className="adminResultSection">
                    {showLoading && (
                        <div className="adminLoadingFrame">
                            <div className="adminLoadingNowPill">
                                <span className="adminLoadingNowDot" />
                                Rendering
                            </div>
                            <ProgressBar durationMs={80000} />
                        </div>
                    )}
                    {!showLoading && showResult && (
                        <div className="adminResultCard">
                            <div className="adminResultMeta">
                                <span className="adminResultBadge"><span className="adminResultBadgeDot" /> Ready</span>
                                <span className="adminResultMetaText">{prompt.slice(0, 60)}{prompt.length > 60 ? '…' : ''}</span>
                            </div>
                            <div className="adminResultCompare adminResultCompare--single">
                                <figure className="adminResultFigure adminResultFigure--after">
                                    <img src={images[0]} alt="Generated" className="adminResultImg" />
                                    <figcaption className="adminResultFigCap">RESULT</figcaption>
                                </figure>
                            </div>
                            <div className="adminResultActions">
                                <button
                                    type="button"
                                    className="adminResultBtn adminResultBtn--primary"
                                    onClick={() => downloadFile(images[0])}
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                                    Download
                                </button>
                                <a
                                    className="adminResultBtn"
                                    href={images[0]}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Open original
                                </a>
                            </div>

                            {taskId && (
                                <div className="adminAnimateBlock">
                                    <span className="adminAnimateLabel">ANIMATE</span>
                                    <div className="adminAnimateGrid">
                                        {ANIMATE_ACTIONS.map(a => (
                                            <button
                                                type="button"
                                                key={a.key}
                                                className={`adminAnimateBtn${animateAction === a.key ? ' adminAnimateBtn--active' : ''}`}
                                                onClick={() => onAnimate(a.key)}
                                                disabled={!!animGenType}
                                            >
                                                <span className="adminAnimateBtnIcon" aria-hidden="true">▶</span>
                                                {a.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Animate video result */}
                    <div ref={animRef} />
                    {(animGenType || animVideo || animateError) && (
                        <div className="adminVideoCard">
                            {animGenType && (
                                <div className="adminLoadingFrame">
                                    <div className="adminLoadingNowPill">
                                        <span className="adminLoadingNowDot" />
                                        Animating · {ANIMATE_ACTIONS.find(a => a.key === animateAction)?.label || animateAction}
                                    </div>
                                    <ProgressBar durationMs={120000} />
                                </div>
                            )}
                            {!animGenType && animVideo && (
                                <>
                                    <div className="adminResultMeta">
                                        <span className="adminResultBadge"><span className="adminResultBadgeDot" /> Video ready</span>
                                        <span className="adminResultMetaText">{ANIMATE_ACTIONS.find(a => a.key === animateAction)?.label || ''}</span>
                                    </div>
                                    <video className="adminVideoPlayer" src={animVideo} autoPlay loop muted playsInline controls />
                                    <div className="adminResultActions">
                                        <button
                                            type="button"
                                            className="adminResultBtn adminResultBtn--primary"
                                            onClick={() => downloadFile(animVideo)}
                                        >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                                            Download video
                                        </button>
                                    </div>
                                </>
                            )}
                            {animateError && !animGenType && (
                                <div className="adminError">
                                    <span className="adminErrorTag">VIDEO</span>
                                    <span>{animateError}</span>
                                </div>
                            )}
                            {animTaskId && animGenType && (
                                <div className="adminVideoTaskHint">task <code>{animTaskId.slice(0, 8)}</code></div>
                            )}
                        </div>
                    )}

                    {error && (
                        <div className="adminError">
                            <span className="adminErrorTag">ERROR</span>
                            <span>{error}</span>
                        </div>
                    )}
                </div>
            )}

            <LoginPopup isOpen={showLogin} setIsOpen={setShowLogin} setSignedIn={setSignedIn} />
            <SubscriptionPopup isOpen={showPricing} onClose={() => setShowPricing(false)} token={token} onNeedLogin={() => { setShowPricing(false); setShowLogin(true); }} />
            <AccountPopup isOpen={showAccount} onClose={() => setShowAccount(false)} token={token} user={user} />

            <SiteFooter onNavigate={onNavigate} />
        </div>
    );
}
